import React, {
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import { Scanner } from "@yudiel/react-qr-scanner";
import { toast } from "react-toastify";
import {
	FiAlertTriangle,
	FiCamera,
	FiCheckCircle,
	FiShield,
	FiSmartphone,
} from "react-icons/fi";
import {
	checkInService,
	CheckInPayload,
} from "../../../services/checkInService";

type BarcodeDetectorResult = {
	rawValue?: string;
};

type BarcodeDetectorConstructor = {
	new (options?: { formats?: string[] }): {
		detect: (source: CanvasImageSource) => Promise<BarcodeDetectorResult[]>;
	};
	getSupportedFormats?: () => Promise<string[]>;
};

declare global {
	interface Window {
		BarcodeDetector?: BarcodeDetectorConstructor;
	}
}

const normalizeToken = (raw: string): string | null => {
	if (!raw) return null;
	const trimmed = raw.trim();
	if (!trimmed) return null;
	const lower = trimmed.toLowerCase();
	const marker = "bpticket:";
	if (lower.includes(marker)) {
		return trimmed.substring(lower.indexOf(marker) + marker.length).trim();
	}
	if (trimmed.startsWith("http")) {
		const parts = trimmed.split(/[/#?]/).filter(Boolean);
		return parts.length ? parts[parts.length - 1] : null;
	}
	return trimmed;
};

const selectDefaultCameraId = (
	devices: MediaDeviceInfo[],
	preferIPhone: boolean
): string | null => {
	if (!devices.length) return null;
	const normalizeLabel = (label: string) => label?.toLowerCase() ?? "";
	if (preferIPhone) {
		const iPhoneCamera = devices.find((device) =>
			normalizeLabel(device.label).includes("iphone")
		);
		if (iPhoneCamera) {
			return iPhoneCamera.deviceId;
		}
	}
	const rearCamera = devices.find((device) => {
		const label = normalizeLabel(device.label);
		return (
			label.includes("back") ||
			label.includes("rear") ||
			label.includes("environment") ||
			label.includes("背面") ||
			label.includes("外")
		);
	});
	return rearCamera?.deviceId ?? devices[0].deviceId;
};

const formatCameraLabel = (device: MediaDeviceInfo, index: number): string => {
	if (device.label) return device.label;
	return `カメラ ${index + 1}`;
};

const isIPhoneCamera = (device: MediaDeviceInfo | null | undefined) =>
	device?.label?.toLowerCase().includes("iphone") ?? false;

const isBuiltInMacCamera = (device: MediaDeviceInfo | null | undefined) => {
	const label = device?.label?.toLowerCase() ?? "";
	return (
		label.includes("facetime") ||
		label.includes("built-in") ||
		label.includes("builtin") ||
		label.includes("integrated") ||
		label.includes("macbook") ||
		label.includes("hd camera")
	);
};

class CameraErrorBoundary extends React.Component<
	{
		onError: (error: Error) => void;
		fallback: React.ReactNode;
		children: React.ReactNode;
	},
	{ hasError: boolean }
> {
	constructor(props: any) {
		super(props);
		this.state = { hasError: false };
	}

	static getDerivedStateFromError() {
		return { hasError: true };
	}

	componentDidCatch(error: Error) {
		this.props.onError(error);
	}

	render() {
		if (this.state.hasError) {
			return this.props.fallback;
		}
		return this.props.children;
	}
}

const OrganizerCheckInPage: React.FC = () => {
	const isMacDevice = useMemo(() => {
		if (typeof navigator === "undefined") return false;
		return /Macintosh|Mac OS X/.test(navigator.userAgent);
	}, []);
	const [cameraActive, setCameraActive] = useState(false);
	const [scannerMode, setScannerMode] = useState<"environment" | "auto">(
		isMacDevice ? "auto" : "environment"
	);
	const [scanLoading, setScanLoading] = useState(false);
	const [cameraError, setCameraError] = useState<string | null>(null);
	const [cameraDevices, setCameraDevices] = useState<MediaDeviceInfo[]>([]);
	const [selectedCameraId, setSelectedCameraId] = useState<string | null>(null);
	const [cameraInitStatus, setCameraInitStatus] = useState<
		"idle" | "pending" | "ready" | "error"
	>("idle");
	const [cameraPermissionDenied, setCameraPermissionDenied] = useState(false);
	const [manualToken, setManualToken] = useState("");
	const [currentToken, setCurrentToken] = useState("");
	const [payload, setPayload] = useState<CheckInPayload | null>(null);
	const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
	const [statusLabel, setStatusLabel] =
		useState<string>("QRを読み取ってください");
	const lastScanRef = useRef<{ token: string; ts: number } | null>(null);
	const [canUseNativeScanner, setCanUseNativeScanner] = useState(false);
	const [nativeScannerVersion, setNativeScannerVersion] = useState(0);
	const [videoAspectRatio, setVideoAspectRatio] = useState(1);
	const [redeemFlashActive, setRedeemFlashActive] = useState(false);
	const videoRef = useRef<HTMLVideoElement | null>(null);
	const streamRef = useRef<MediaStream | null>(null);
	const rafRef = useRef<number | null>(null);
	const isMountedRef = useRef(true);
	const restartNativeScanner = useCallback(() => {
		setNativeScannerVersion((prev) => prev + 1);
	}, []);
	useEffect(() => {
		return () => {
			isMountedRef.current = false;
		};
	}, []);

	useEffect(() => {
		if (!redeemFlashActive) return;
		const timer = setTimeout(() => setRedeemFlashActive(false), 1500);
		return () => clearTimeout(timer);
	}, [redeemFlashActive]);

	useEffect(() => {
		if (typeof window === "undefined") return;
		setCanUseNativeScanner(typeof window.BarcodeDetector === "function");
	}, []);

	const refreshCameraDevices = useCallback(
		async (requestPermission = false) => {
			if (typeof navigator === "undefined" || !navigator.mediaDevices) {
				if (!isMountedRef.current) return;
				setCameraError("カメラAPIに対応していないブラウザです。");
				setCameraInitStatus("error");
				setCameraActive(false);
				return;
			}
			if (requestPermission) {
				setCameraInitStatus("pending");
			}
			try {
				if (requestPermission) {
					const stream = await navigator.mediaDevices.getUserMedia({
						video: true,
					});
					stream.getTracks().forEach((track) => track.stop());
				}
				const devices = await navigator.mediaDevices.enumerateDevices();
				if (!isMountedRef.current) return;
				const videoInputs = devices.filter(
					(device) => device.kind === "videoinput"
				);
				setCameraDevices(videoInputs);
				if (!videoInputs.length) {
					setCameraError("利用できるカメラが見つかりません。");
					setCameraInitStatus("error");
					setCameraActive(false);
					return;
				}
				setCameraInitStatus("ready");
				setCameraPermissionDenied(false);
				setCameraError(null);
				setCameraActive(true);
				setSelectedCameraId((current) => {
					if (
						current &&
						videoInputs.some((device) => device.deviceId === current)
					) {
						return current;
					}
					return (
						selectDefaultCameraId(videoInputs, isMacDevice) ??
						videoInputs[0].deviceId
					);
				});
			} catch (error: any) {
				console.error(error);
				if (!isMountedRef.current) return;
				setCameraInitStatus("error");
				setCameraActive(false);
				if (
					error?.name === "NotAllowedError" ||
					error?.name === "SecurityError"
				) {
					setCameraPermissionDenied(true);
					setCameraError(
						"カメラの利用が拒否されています。ブラウザの設定から許可してください。"
					);
				} else if (error?.name === "NotFoundError") {
					setCameraError("利用できるカメラが見つかりません。");
				} else {
					setCameraError(error?.message ?? "カメラの初期化に失敗しました。");
				}
			}
		},
		[isMacDevice]
	);

	useEffect(() => {
		if (typeof navigator === "undefined" || !navigator.mediaDevices) {
			setCameraError("カメラAPIに対応していないブラウザです。");
			setCameraInitStatus("error");
			setCameraActive(false);
			return;
		}
		let unmounted = false;
		const run = async () => {
			if (!unmounted) {
				await refreshCameraDevices(false);
			}
		};
		run();

		const mediaDevices = navigator.mediaDevices;
		const handleDeviceChange = () => {
			if (!unmounted) {
				refreshCameraDevices(false);
			}
		};
		if (mediaDevices.addEventListener) {
			mediaDevices.addEventListener("devicechange", handleDeviceChange);
		} else if ("ondevicechange" in mediaDevices) {
			(
				mediaDevices as unknown as { ondevicechange: (() => void) | null }
			).ondevicechange = handleDeviceChange;
		}
		return () => {
			unmounted = true;
			if (mediaDevices.removeEventListener) {
				mediaDevices.removeEventListener("devicechange", handleDeviceChange);
			} else if ("ondevicechange" in mediaDevices) {
				(
					mediaDevices as unknown as { ondevicechange: (() => void) | null }
				).ondevicechange = null;
			}
		};
	}, [refreshCameraDevices]);

	const iphoneCamera = useMemo(
		() => cameraDevices.find((device) => isIPhoneCamera(device)) ?? null,
		[cameraDevices]
	);

	const macBuiltInCamera = useMemo(
		() => cameraDevices.find((device) => isBuiltInMacCamera(device)) ?? null,
		[cameraDevices]
	);

	const buildVideoConstraints = useCallback((): MediaTrackConstraints => {
		const constraints: MediaTrackConstraints = {
			width: { ideal: 1280 },
			height: { ideal: 720 },
		};
		if (selectedCameraId) {
			constraints.deviceId = { exact: selectedCameraId };
		} else if (scannerMode === "environment") {
			constraints.facingMode = { ideal: "environment" };
		}
		return constraints;
	}, [scannerMode, selectedCameraId]);

	const scannerConstraints = useMemo<MediaTrackConstraints>(() => {
		return buildVideoConstraints();
	}, [buildVideoConstraints]);

	useEffect(() => {
		if (!canUseNativeScanner) return;
		if (typeof navigator === "undefined" || !navigator.mediaDevices) {
			setCameraError("カメラAPIに対応していないブラウザです。");
			setCameraInitStatus("error");
			setCameraActive(false);
			return;
		}
		let cancelled = false;

		const startStream = async () => {
			try {
				setCameraInitStatus("pending");
				const stream = await navigator.mediaDevices.getUserMedia({
					video: buildVideoConstraints(),
					audio: false,
				});
				if (cancelled) {
					stream.getTracks().forEach((track) => track.stop());
					return;
				}
				streamRef.current?.getTracks().forEach((track) => track.stop());
				streamRef.current = stream;
				const videoElement = videoRef.current;
				if (videoElement) {
					videoElement.srcObject = stream;
					await videoElement.play().catch(() => undefined);
				}
				setCameraError(null);
				setCameraActive(true);
				setCameraInitStatus("ready");
				setCameraPermissionDenied(false);
				stream.getVideoTracks().forEach((track) => {
					track.onended = () => {
						if (!cancelled) {
							restartNativeScanner();
						}
					};
				});
				await refreshCameraDevices(false);
			} catch (error: any) {
				console.error(error);
				if (cancelled) return;
				setCameraInitStatus("error");
				setCameraActive(false);
				if (
					error?.name === "NotAllowedError" ||
					error?.name === "SecurityError"
				) {
					setCameraPermissionDenied(true);
					setCameraError(
						"カメラの利用が拒否されています。ブラウザの設定から許可してください。"
					);
				} else if (error?.name === "NotFoundError") {
					setCameraError("利用できるカメラが見つかりません。");
				} else {
					setCameraError(error?.message ?? "カメラの初期化に失敗しました。");
				}
			}
		};

		startStream();

		return () => {
			cancelled = true;
			streamRef.current?.getTracks().forEach((track) => track.stop());
			streamRef.current = null;
		};
	}, [
		buildVideoConstraints,
		canUseNativeScanner,
		restartNativeScanner,
		refreshCameraDevices,
		nativeScannerVersion,
	]);

	useEffect(() => {
		if (!canUseNativeScanner) return;
		const stream = streamRef.current;
		const videoElement = videoRef.current;
		if (stream && videoElement) {
			if (videoElement.srcObject !== stream) {
				videoElement.srcObject = stream;
			}
			const playVideo = async () => {
				try {
					await videoElement.play();
				} catch (error) {
					console.error("Video play failed", error);
				}
			};
			playVideo();
		}
	}, [
		canUseNativeScanner,
		cameraActive,
		selectedCameraId,
		scannerMode,
		nativeScannerVersion,
	]);

	useEffect(() => {
		const videoElement = videoRef.current;
		if (!videoElement) return;
		const updateAspectRatio = () => {
			if (videoElement.videoWidth && videoElement.videoHeight) {
				setVideoAspectRatio(
					Math.max(0.1, videoElement.videoWidth / videoElement.videoHeight)
				);
			}
		};
		videoElement.addEventListener("loadedmetadata", updateAspectRatio);
		videoElement.addEventListener("resize", updateAspectRatio);
		updateAspectRatio();
		return () => {
			videoElement.removeEventListener("loadedmetadata", updateAspectRatio);
			videoElement.removeEventListener("resize", updateAspectRatio);
		};
	}, [nativeScannerVersion, cameraActive]);

	const activeCameraLabel = useMemo(() => {
		if (selectedCameraId) {
			const index = cameraDevices.findIndex(
				(device) => device.deviceId === selectedCameraId
			);
			if (index >= 0) {
				return formatCameraLabel(cameraDevices[index], index);
			}
			return "選択中のカメラ";
		}
		return scannerMode === "environment"
			? "背面カメラ（自動）"
			: "利用可能なカメラ（自動）";
	}, [cameraDevices, scannerMode, selectedCameraId]);
	const previewAspectRatio = useMemo(() => {
		if (Number.isFinite(videoAspectRatio) && videoAspectRatio > 0) {
			return videoAspectRatio;
		}
		return 4 / 3;
	}, [videoAspectRatio]);
	const remainingValidTickets = useMemo(() => {
		if (!payload) return 0;
		return payload.aggregateCounts?.valid ?? payload.counts?.valid ?? 0;
	}, [payload]);
	const isFullyRedeemed = useMemo(
		() => remainingValidTickets === 0 && !!payload,
		[remainingValidTickets, payload]
	);
	const overlayVisible = redeemFlashActive || isFullyRedeemed;

	const triggerScan = useCallback(async (token: string) => {
		try {
			setScanLoading(true);
			setStatusLabel("読み取り中...");
			const response = await checkInService.scan(token);
			setCurrentToken(token);
			setManualToken("");
			setPayload(response.payload);
			setSelectedIds(new Set(response.payload.validTickets.map((t) => t.id)));
			if (response.state === "auto_redeemed") {
				toast.success("チケットを自動で入場処理しました");
				setStatusLabel("1枚のチケットを入場済みにしました");
				setRedeemFlashActive(true);
			} else if (response.state === "already_redeemed") {
				toast.info("このQRに紐づくチケットはすべて入場済みです");
				setStatusLabel("すべて使用済みのチケットです");
			} else {
				toast.success("チケット情報を取得しました");
				setStatusLabel("入場させるチケットを選択してください");
			}
		} catch (error: any) {
			console.error(error);
			toast.error(
				error?.response?.data?.message || "QRの読み取りに失敗しました"
			);
			setStatusLabel("QRを読み取ってください");
		} finally {
			setScanLoading(false);
		}
	}, []);

	const handleScanValue = useCallback(
		async (value: string) => {
			const token = normalizeToken(value);
			if (!token || scanLoading) return;
			const now = Date.now();
			if (
				lastScanRef.current &&
				lastScanRef.current.token === token &&
				now - lastScanRef.current.ts < 2000
			) {
				return;
			}
			lastScanRef.current = { token, ts: now };
			await triggerScan(token);
		},
		[scanLoading, triggerScan]
	);

	useEffect(() => {
		if (!canUseNativeScanner) return;
		if (typeof window === "undefined" || !window.BarcodeDetector) return;
		let cancelled = false;
		let detector: {
			detect: (source: CanvasImageSource) => Promise<BarcodeDetectorResult[]>;
		} | null = null;

		const initDetector = async () => {
			try {
				const formats =
					typeof window.BarcodeDetector.getSupportedFormats === "function"
						? await window.BarcodeDetector.getSupportedFormats()
						: undefined;
				detector = new window.BarcodeDetector(
					formats && formats.length ? { formats } : undefined
				);
			} catch (error) {
				console.error("BarcodeDetector initialization failed", error);
				detector = null;
			}
		};

		const scan = async () => {
			if (cancelled) return;
			if (!detector) {
				await initDetector();
				rafRef.current = requestAnimationFrame(scan);
				return;
			}
			if (!cameraActive) {
				rafRef.current = requestAnimationFrame(scan);
				return;
			}
			const videoElement = videoRef.current;
			if (!videoElement || videoElement.readyState < 2) {
				rafRef.current = requestAnimationFrame(scan);
				return;
			}
			try {
				const barcodes = await detector.detect(videoElement);
				if (barcodes.length > 0) {
					const value = barcodes[0]?.rawValue;
					if (value) {
						await handleScanValue(value);
					}
				}
			} catch (error) {
				console.error("Barcode detection failed", error);
			}
			rafRef.current = requestAnimationFrame(scan);
		};

		rafRef.current = requestAnimationFrame(scan);

		return () => {
			cancelled = true;
			if (rafRef.current) {
				cancelAnimationFrame(rafRef.current);
				rafRef.current = null;
			}
		};
	}, [cameraActive, canUseNativeScanner, handleScanValue]);

	const handleCameraComponentError = useCallback(
		(error: Error) => {
			console.error(error);
			const message = error?.message ?? "カメラの初期化に失敗しました。";
			if (message.includes("Requested device not found")) {
				const fallbackCandidates = cameraDevices.filter(
					(device) => device.deviceId !== selectedCameraId
				);
				const fallbackId =
					selectDefaultCameraId(fallbackCandidates, isMacDevice) ??
					fallbackCandidates[0]?.deviceId ??
					cameraDevices[0]?.deviceId ??
					null;
				if (fallbackId) {
					setCameraError(
						"選択したカメラが見つからないため自動で切り替えました。"
					);
					setSelectedCameraId(fallbackId);
					setScannerMode("auto");
					refreshCameraDevices(false);
					return;
				}
				setScannerMode("auto");
				setSelectedCameraId(null);
				refreshCameraDevices(false);
			}
			setCameraError(message);
			setCameraActive(false);
		},
		[cameraDevices, isMacDevice, refreshCameraDevices, selectedCameraId]
	);

	const handleScannerError = useCallback(
		(error: unknown) => {
			console.error(error);
			const message = (error as Error)?.message ?? "カメラ取得に失敗しました。";
			if (message.includes("Requested device not found")) {
				const fallbackCandidates = cameraDevices.filter(
					(device) => device.deviceId !== selectedCameraId
				);
				const fallbackId =
					selectDefaultCameraId(fallbackCandidates, isMacDevice) ??
					fallbackCandidates[0]?.deviceId ??
					cameraDevices[0]?.deviceId ??
					null;
				if (fallbackId) {
					setCameraError("利用可能なカメラに切り替えました。");
					setSelectedCameraId(fallbackId);
				} else {
					setSelectedCameraId(null);
					setCameraError(
						"利用できるカメラが見つからないため自動スキャンに切り替えました。"
					);
					setScannerMode("auto");
				}
				refreshCameraDevices(false);
				return;
			}
			setCameraError(message);
			setCameraActive(false);
		},
		[cameraDevices, isMacDevice, refreshCameraDevices, selectedCameraId]
	);

	const primaryValidTickets = payload?.validTickets ?? [];
	const redeemedTickets = payload?.redeemedTickets ?? [];
	const relatedOrders = payload?.relatedOrders ?? [];
	const selectableTickets =
		payload?.aggregateValidTickets ?? primaryValidTickets;
	const relatedValidTickets = useMemo(() => {
		if (!payload) return [] as typeof selectableTickets;
		const primaryOrderItemId = payload.orderItemId;
		return selectableTickets.filter((ticket) => {
			if (ticket.source) {
				return ticket.source === "related";
			}
			return (ticket.orderItemId ?? primaryOrderItemId) !== primaryOrderItemId;
		});
	}, [payload, selectableTickets]);

	const handleRedeem = async (mode: "all" | "selected") => {
		if (!payload || !currentToken) return;
		const ids =
			mode === "all"
				? selectableTickets.map((t) => t.id)
				: Array.from(selectedIds);
		if (ids.length === 0) {
			toast.warn("入場させるチケットを選択してください");
			return;
		}
		try {
			setScanLoading(true);
			const response = await checkInService.redeem(
				currentToken,
				ids,
				mode === "all"
			);
			setPayload(response.payload);
			setSelectedIds(new Set(response.payload.validTickets.map((t) => t.id)));
			if (response.state === "redeemed") {
				toast.success("入場処理が完了しました");
				setStatusLabel("入場処理が完了しました");
				setRedeemFlashActive(true);
			} else {
				toast.info(response.message || "すでに入場済みのチケットです");
				setStatusLabel("すでに入場済みです");
			}
		} catch (error: any) {
			console.error(error);
			toast.error(error?.response?.data?.message || "入場処理に失敗しました");
		} finally {
			setScanLoading(false);
		}
	};

	const stats = useMemo(() => {
		if (!payload) return { total: 0, valid: 0, redeemed: 0 };
		if (payload.aggregateCounts) {
			return payload.aggregateCounts;
		}
		return {
			total: payload.counts.total,
			valid: payload.counts.valid,
			redeemed: payload.counts.redeemed,
		};
	}, [payload]);

	const toggleTicketSelection = (ticketId: number) => {
		setSelectedIds((prev) => {
			const next = new Set(prev);
			if (next.has(ticketId)) {
				next.delete(ticketId);
			} else {
				next.add(ticketId);
			}
			return next;
		});
	};

	const handleManualSubmit = async (event: React.FormEvent) => {
		event.preventDefault();
		if (!manualToken.trim()) return;
		const token = normalizeToken(manualToken);
		if (!token) {
			toast.error("QRコード文字列が不正です");
			return;
		}
		await triggerScan(token);
	};

	return (
		<div className="container mx-auto max-w-5xl p-6 min-h-screen">
			<div className="space-y-6">
				<div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-700 dark:bg-gray-900">
					<div className="flex flex-col gap-6">
						<div className="space-y-4">
							<div>
								<h2 className="text-2xl font-bold text-gray-900 dark:text-white">
									入場QRコード読み取り
								</h2>
								<p className="text-sm text-gray-500">
									端末の背面カメラでQRコードを読み取ると、同じ注文に含まれるチケット枚数をまとめて確認できます。
								</p>
							</div>
							<div className="space-y-2 rounded-2xl border border-gray-100 bg-gray-50 p-4 text-sm dark:border-gray-800 dark:bg-gray-800/60">
								<div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
									<div>
										<p className="text-xs uppercase tracking-wide text-gray-500">
											現在のカメラ
										</p>
										<p className="text-base font-semibold text-gray-900 dark:text-white">
											{activeCameraLabel}
										</p>
										{cameraInitStatus === "pending" && (
											<p className="text-xs text-gray-500">
												カメラを初期化しています...
											</p>
										)}
									</div>
									<select
										className="w-full rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm dark:border-gray-700 dark:bg-gray-900 sm:w-60"
										value={selectedCameraId ?? ""}
										onChange={(event) => {
											const value = event.target.value;
											if (!value) {
												setSelectedCameraId(null);
												setScannerMode("auto");
												return;
											}
											setCameraError(null);
											setCameraActive(true);
											setSelectedCameraId(value);
										}}
										disabled={!cameraDevices.length}
									>
										<option value="" disabled>
											{cameraDevices.length ? "カメラを選択" : "カメラを検出中"}
										</option>
										{cameraDevices.map((device, index) => (
											<option
												key={device.deviceId || `camera-${index}`}
												value={device.deviceId}
											>
												{formatCameraLabel(device, index)}
											</option>
										))}
									</select>
								</div>
								{iphoneCamera && selectedCameraId !== iphoneCamera.deviceId && (
									<button
										type="button"
										onClick={() => {
											setSelectedCameraId(iphoneCamera.deviceId);
											setScannerMode("auto");
											setCameraError(null);
											setCameraActive(true);
										}}
										className="inline-flex items-center gap-2 text-xs font-semibold text-violet-600 hover:underline"
									>
										<FiSmartphone /> iPhoneカメラに切り替える
									</button>
								)}
								{isMacDevice &&
									macBuiltInCamera &&
									selectedCameraId !== macBuiltInCamera.deviceId && (
										<button
											type="button"
											onClick={() => {
												setSelectedCameraId(macBuiltInCamera.deviceId);
												setScannerMode("auto");
												setCameraError(null);
												setCameraActive(true);
											}}
											className="inline-flex items-center gap-2 text-xs font-semibold text-violet-600 hover:underline"
										>
											<FiCamera /> Mac本体のカメラを使う
										</button>
									)}
								{isMacDevice && (
									<p className="text-xs text-gray-500 dark:text-gray-300">
										MacではContinuity
										Cameraを使ってiPhoneのカメラを利用できます。iPhoneをロック解除してMacの近くに置くとカメラ一覧に表示され、ボタンから本体/外部カメラを切り替えられます。
									</p>
								)}
							</div>
							{cameraPermissionDenied && (
								<p className="text-xs text-red-500">
									ブラウザのURLバー横のカメラアイコンからアクセスを許可してください。
								</p>
							)}
							{cameraActive ? (
								<div
									className="relative overflow-hidden rounded-xl border border-gray-200 bg-black/80 shadow-inner dark:border-gray-800"
									style={{
										aspectRatio: previewAspectRatio,
										minHeight: "320px",
									}}
								>
									<div className="pointer-events-none absolute left-4 top-4 z-10 rounded-full bg-black/60 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-white">
										リアルタイムプレビュー
									</div>
									{canUseNativeScanner ? (
										<video
											ref={videoRef}
											className={`h-full w-full object-cover transition-opacity duration-300 ${
												overlayVisible ? "opacity-40" : "opacity-100"
											}`}
											muted
											autoPlay
											playsInline
										/>
									) : (
										<div
											className={`h-full w-full transition-opacity duration-300 ${
												overlayVisible ? "opacity-40" : "opacity-100"
											}`}
										>
											<CameraErrorBoundary
												onError={handleCameraComponentError}
												fallback={
													<div className="flex h-64 items-center justify-center text-sm text-red-400">
														カメラの初期化に失敗しました
													</div>
												}
											>
												<Scanner
													key={`${
														selectedCameraId ?? scannerMode
													}-${nativeScannerVersion}`}
													styles={{
														container: {
															width: "100%",
															height: "100%",
														},
														video: {
															width: "100%",
															height: "100%",
															objectFit: "cover",
															backgroundColor: "#000",
														},
													}}
													allowMultiple={false}
													constraints={scannerConstraints}
													onScan={(detected) => {
														const codes = Array.isArray(detected)
															? detected
															: [];
														if (codes.length > 0 && codes[0]?.rawValue) {
															handleScanValue(codes[0].rawValue);
														}
													}}
													onError={handleScannerError}
												/>
											</CameraErrorBoundary>
										</div>
									)}
									<div
										className={`pointer-events-none absolute inset-0 flex items-center justify-center transition-opacity duration-300 ${
											overlayVisible ? "opacity-100" : "opacity-0"
										}`}
									>
										<div className="flex h-32 w-32 items-center justify-center rounded-full bg-white/80 text-emerald-600 shadow-lg">
											<FiCheckCircle className="h-16 w-16" />
										</div>
									</div>
									<div className="pointer-events-none absolute inset-0 flex items-center justify-center">
										<div className="h-40 w-40"></div>
									</div>
								</div>
							) : (
								<div className="rounded-xl border border-dashed border-gray-300 p-8 text-center text-gray-500 dark:border-gray-700">
									<FiCamera className="mx-auto h-8 w-8 text-gray-400" />
									<p className="mt-2 text-sm">
										カメラを有効化するとQR読み取りを再開できます。
									</p>
									{cameraError && (
										<p className="text-xs text-red-500">{cameraError}</p>
									)}
									<button
										type="button"
										onClick={() => {
											setCameraError(null);
											if (canUseNativeScanner) {
												restartNativeScanner();
												refreshCameraDevices(false);
											} else {
												refreshCameraDevices(true);
											}
										}}
										className="mt-4 inline-flex items-center gap-2 rounded-full bg-gray-900 px-3 sm:px-4 py-2 text-sm font-semibold text-white dark:bg-white dark:text-gray-900"
									>
										<FiCamera /> カメラを再初期化
									</button>
								</div>
							)}
						</div>
						<form onSubmit={handleManualSubmit} className="w-full space-y-4">
							<label className="block text-xs font-semibold uppercase tracking-wide text-gray-500">
								手動入力
							</label>
							<input
								type="text"
								value={manualToken}
								onChange={(e) => setManualToken(e.target.value)}
								placeholder="QRコード文字列を貼り付け"
								className="w-full rounded-2xl border border-gray-200 px-4 py-3 text-sm focus:border-violet-500 focus:ring-2 focus:ring-violet-200 dark:border-gray-700 dark:bg-gray-800"
							/>
							<button
								type="submit"
								className="w-full rounded-2xl bg-gradient-to-r from-violet-500 to-purple-600 py-3 text-sm font-semibold text-white shadow-lg shadow-violet-500/30"
								disabled={scanLoading}
							>
								{scanLoading ? "照会中..." : "QRコードを照会"}
							</button>
							<div className="rounded-2xl bg-violet-50 p-4 text-xs text-violet-700 dark:bg-violet-500/10 dark:text-violet-200">
								<p>
									※
									同じ注文・チケット種別でまとめて購入された枚数でも、1つのQRで全て確認できます。
								</p>
							</div>
						</form>
					</div>
				</div>

				{payload && (
					<div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-700 dark:bg-gray-900">
						<div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
							<div>
								<p className="text-xs uppercase tracking-[0.25em] text-gray-500">
									Scan Result
								</p>
								<h3 className="text-xl font-bold text-gray-900 dark:text-white">
									{payload.event.title ?? "イベント情報"}
								</h3>
								<p className="text-sm text-gray-500">
									{payload.ticketType.name ?? "チケット"} /{" "}
									{payload.event.venue ?? "会場未設定"}
								</p>
							</div>
							<div className="rounded-2xl bg-gray-50 px-5 py-3 text-sm text-gray-600 dark:bg-gray-800 dark:text-gray-300">
								{statusLabel}
							</div>
						</div>
						<div className="mt-6 grid gap-4 sm:grid-cols-3">
							<div className="rounded-2xl bg-gray-50 p-4 text-center dark:bg-gray-800">
								<p className="text-xs text-gray-500">合計枚数</p>
								<p className="text-2xl font-bold text-gray-900 dark:text-white">
									{stats.total}
								</p>
							</div>
							<div className="rounded-2xl bg-emerald-50 p-4 text-center text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-200">
								<p className="text-xs">未使用</p>
								<p className="text-2xl font-bold">{stats.valid}</p>
							</div>
							<div className="rounded-2xl bg-gray-100 p-4 text-center text-gray-600 dark:bg-gray-800 dark:text-gray-300">
								<p className="text-xs">入場済み</p>
								<p className="text-2xl font-bold">{stats.redeemed}</p>
							</div>
						</div>

						<div className="mt-8 grid gap-6 lg:grid-cols-2">
							<div>
								<div className="flex items-center justify-between">
									<h4 className="text-sm font-semibold text-gray-900 dark:text-white">
										入場対象のチケット
									</h4>
									<button
										onClick={() =>
											setSelectedIds(
												new Set(primaryValidTickets.map((t) => t.id))
											)
										}
										className="text-xs text-violet-600 hover:underline"
									>
										すべて選択
									</button>
								</div>
								{primaryValidTickets.length === 0 ? (
									<div className="mt-4 rounded-2xl border border-dashed border-gray-300 p-4 text-center text-sm text-gray-500">
										未使用のチケットはありません
									</div>
								) : (
									<div className="mt-4 space-y-3 max-h-72 overflow-y-auto pr-1">
										{primaryValidTickets.map((ticket) => (
											<label
												key={ticket.id}
												className="flex cursor-pointer items-center justify-between rounded-2xl border border-gray-200 px-4 py-3 hover:border-violet-400 dark:border-gray-700"
											>
												<div>
													<p className="text-sm font-semibold text-gray-900 dark:text-white">
														#{ticket.sequenceNumber.toString().padStart(3, "0")}
													</p>
													<p className="text-xs text-gray-500">未使用</p>
												</div>
												<input
													type="checkbox"
													checked={selectedIds.has(ticket.id)}
													onChange={() => toggleTicketSelection(ticket.id)}
													className="h-4 w-4 rounded border-gray-300 text-violet-600 focus:ring-violet-500"
												/>
											</label>
										))}
									</div>
								)}

								<div className="mt-4 flex flex-wrap gap-3">
									<button
										onClick={() => handleRedeem("all")}
										disabled={stats.valid === 0 || scanLoading}
										className="inline-flex flex-1 items-center justify-center gap-2 rounded-2xl bg-gray-900 px-4 py-3 text-sm font-semibold text-white disabled:opacity-50 dark:bg-white dark:text-gray-900"
									>
										<FiShield /> 有効なチケットをすべて入場
									</button>
									<button
										onClick={() => handleRedeem("selected")}
										disabled={selectedIds.size === 0 || scanLoading}
										className="inline-flex flex-1 items-center justify-center gap-2 rounded-2xl border border-violet-500 px-4 py-3 text-sm font-semibold text-violet-600 disabled:opacity-50"
									>
										<FiCheckCircle /> 選択したチケットを入場
									</button>
								</div>

								{relatedValidTickets.length > 0 && (
									<div className="mt-6">
										<div className="flex items-center justify-between">
											<h5 className="text-xs font-semibold uppercase tracking-wide text-gray-500">
												同じ購入者の他のチケット
											</h5>
											<button
												onClick={() =>
													setSelectedIds((prev) => {
														const next = new Set(prev);
														relatedValidTickets.forEach((ticket) =>
															next.add(ticket.id)
														);
														return next;
													})
												}
												className="text-xs text-violet-600 hover:underline"
											>
												すべて選択
											</button>
										</div>
										<p className="mt-1 text-xs text-gray-500">
											本人確認済みの場合に限り、同じ購入者が保有する同種チケットをまとめて入場できます。
										</p>
										<div className="mt-4 space-y-3 max-h-64 overflow-y-auto pr-1">
											{relatedValidTickets.map((ticket) => {
												const relatedOrder = relatedOrders.find(
													(order) => order.orderItemId === ticket.orderItemId
												);
												const ownerLabel =
													relatedOrder?.buyer?.name ??
													payload?.buyer?.name ??
													"同一購入者";
												const qrLabel = ticket.orderItemQrToken
													? `${ticket.orderItemQrToken.slice(
															0,
															4
													  )}...${ticket.orderItemQrToken.slice(-4)}`
													: "";
												return (
													<label
														key={`related-${ticket.id}`}
														className="flex cursor-pointer items-center justify-between rounded-2xl border border-dashed border-gray-300 px-4 py-3 hover:border-violet-400 dark:border-gray-700"
													>
														<div>
															<p className="text-sm font-semibold text-gray-900 dark:text-white">
																#
																{ticket.sequenceNumber
																	.toString()
																	.padStart(3, "0")}{" "}
																/ 注文ID {ticket.orderItemId ?? "-"}
															</p>
															<p className="text-xs text-gray-500">
																{ownerLabel}
																{qrLabel && ` ・QR ${qrLabel}`}
															</p>
														</div>
														<input
															type="checkbox"
															checked={selectedIds.has(ticket.id)}
															onChange={() => toggleTicketSelection(ticket.id)}
															className="h-4 w-4 rounded border-gray-300 text-violet-600 focus:ring-violet-500"
														/>
													</label>
												);
											})}
										</div>
									</div>
								)}
							</div>
							<div>
								<h4 className="text-sm font-semibold text-gray-900 dark:text-white">
									入場済みの履歴
								</h4>
								{redeemedTickets.length === 0 ? (
									<div className="mt-4 rounded-2xl border border-dashed border-gray-300 p-4 text-sm text-gray-500">
										まだ入場処理されたチケットはありません。
									</div>
								) : (
									<div className="mt-4 space-y-3 max-h-72 overflow-y-auto pr-1">
										{redeemedTickets.map((ticket) => (
											<div
												key={`redeemed-${ticket.id}`}
												className="rounded-2xl border border-gray-100 bg-gray-50 px-4 py-3 text-sm text-gray-600 dark:border-gray-800 dark:bg-gray-800"
											>
												#{ticket.sequenceNumber.toString().padStart(3, "0")}{" "}
												を入場済み
											</div>
										))}
									</div>
								)}
							</div>
						</div>
					</div>
				)}
				{!payload && (
					<div className="rounded-xl border border-dashed border-gray-300 p-6 text-center text-sm text-gray-500 dark:border-gray-700">
						<FiAlertTriangle className="mx-auto h-6 w-6 text-gray-400" />
						<p className="mt-2">
							QRを読み取ると、ここにチケットの詳細が表示されます。
						</p>
					</div>
				)}
			</div>
		</div>
	);
};

export default OrganizerCheckInPage;
